/**
 * FATE Cart Recommendations Controller
 * ───────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for cart drawer recommendations.
 *
 * Architecture principles:
 *   1. IDEMPOTENT    — instantiated once (window.__fateRecsController guard).
 *   2. SIGNATURE CACHE — caches last-rendered cart signature (sorted handles).
 *                      Skips all network + DOM work when signature unchanged.
 *   3. DEBOUNCED    — coalesces events within 150ms window.
 *   4. STATE MACHINE — idle → fetching → rendered. Never overlapping fetches.
 *   5. EVENT DELEGATION — click handler bound ONCE on grid (not per-variant).
 *   6. NO POLLING, NO TIMERS — reacts only to events.
 *   7. KEY-BASED DIFF — on re-render, preserves existing DOM nodes for
 *                      products that are still in the list (matched by
 *                      data-morph-key=<productId>). Only new products are
 *                      created, only removed products are deleted. Prevents
 *                      the "whole recs section reloads" visual flash.
 *
 * Public API:
 *   window.FateCartRecs.sync()        — force sync (reads cart, refetches if needed)
 *   window.FateCartRecs.invalidate()  — drop cache, force next sync to refetch
 *   window.FateCartRecs.getState()    — introspection for debugging
 *
 * Events listened:
 *   'cart:update' / 'cart:add' / 'cart:change' / 'cart:refresh'
 *   'fate:recs:sync' (explicit trigger)
 *
 * Events emitted:
 *   'fate:recs:rendered' (detail: { signature, count })
 *   'fate:recs:empty' (no recs to show)
 * ───────────────────────────────────────────────────────────────────────
 */

const SIZE_KEYWORDS = ['size', 'talla', 'tamaño', 'sz', 'medida'];
const EXCLUDED_KEYWORDS = ['mystery', 'mystery box', 'caja misteriosa', 'mystery-box', 'seguro'];
/* Solo se recomiendan productos de la marca (tag F4T3). Esto deja FUERA el
   seguro de envío (handle 'seguro-de-envio', sin este tag) y cualquier
   producto no-marca que Shopify pudiera relacionar. Match case-insensitive. */
const BRAND_TAG = 'f4t3';
const DEBOUNCE_MS = 150;
const CART_FETCH_DEDUPE_MS = 1000;
/* Recs cache TTL — invalidates per-product recommendations after 60s so
   stock changes mid-session don't leave stale "available: true" entries.
   Fix for audit M16. */
const RECS_CACHE_TTL_MS = 60_000;

class FateCartRecsController {
  constructor() {
    /** @type {string|null} — signature of last successful render: sorted cart handles joined */
    this._lastSignature = null;
    /** @type {'idle'|'fetching'|'rendering'} */
    this._state = 'idle';
    /** @type {Map<string, Array>} — cache: productId -> recommended products */
    this._recsCache = new Map();
    /** @type {{products: Array, ts: number}|null} — cached fallback collection products with TTL */
    this._fallbackCache = null;
    /** @type {number|null} — debounce timer */
    this._debounceTimer = null;
    /** @type {Promise<object>|null} — in-flight cart.js fetch (dedupe) */
    this._cartPromise = null;
    this._cartPromiseTs = 0;
    /** @type {Element|null} — grid node we attached delegation to.
        Used to detect orphan listener when the grid element gets replaced
        (e.g. theme editor design-mode rerenders, Foxify section reloads). */
    this._delegationGrid = null;
    /** @type {number} — max items to render */
    this._maxItems = 8;
    this._imageObserver = null;
    this._imageRoot = null;
    /** @type {Array} — reserve pool of candidates not yet shown.
        Used for infinite replenishment when all visible recs get added to cart. */
    this._pool = [];
    /** @type {Set<string>} — IDs ever shown (to exclude on future refetches) */
    this._shownIds = new Set();
    /** @type {boolean} — set when a sync arrives while another is in-flight;
        we re-queue once the active one finishes. Fix for audit H1. */
    this._dirty = false;

    this._bindEvents();
    /* Initial render on page load */
    this._queueSync('init');
  }

  /* ═══════════════════ PUBLIC API ═══════════════════ */

  sync() { this._queueSync('api'); }
  invalidate() {
    this._lastSignature = null;
    this._recsCache.clear();
    this._fallbackCache = null;
    this._pool = [];
    this._shownIds.clear();
  }
  getState() {
    return {
      state: this._state,
      signature: this._lastSignature,
      cacheSize: this._recsCache.size,
      hasFallback: !!this._fallbackCache,
    };
  }

  /* ═══════════════════ EVENT WIRING ═══════════════════ */

  _bindEvents() {
    const events = ['cart:update', 'cart:add', 'cart:change', 'cart:refresh', 'fate:recs:sync'];
    for (const ev of events) {
      document.addEventListener(ev, () => this._queueSync(ev));
    }
  }

  _queueSync(reason) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      this._sync(reason).catch(err => console.warn('[FateCartRecs] sync error:', err));
    }, DEBOUNCE_MS);
  }

  /* ═══════════════════ SYNC FLOW ═══════════════════ */

  async _sync(reason) {
    if (this._state !== 'idle') {
      /* Overlapping sync — mark dirty so the active one re-queues on completion. */
      this._dirty = true;
      return;
    }

    const els = this._getEls();
    if (!els.grid) return; /* drawer not rendered yet */

    this._state = 'fetching';
    try {
      const cart = await this._fetchCart();
      const handles = cart.items.map(i => i.handle);
      const signature = this._makeSignature(handles);
      /* "alreadyRendered" = at least one real rec item in grid.
         The grid also contains a <template id="cart-recs-fallback-collection">
         sibling — don't count that as a rendered item. */
      const alreadyRendered = els.grid.querySelector('.cart-recs__item') !== null;

      /* ─────────────────── STICKY MODE ───────────────────
         If the grid already has rendered recommendations, do NOT re-fetch
         a new list when the cart changes. Instead:
           - Re-filter the already-rendered list against the new cart
             (hides products now in cart, reveals products no longer in cart).
           - Update `--in-cart` state on existing items (they keep the added
             button state for UX clarity).

         Why: Shopify's recommendations API returns DIFFERENT related
         products for different cart compositions. Fetching on every cart
         change makes the whole recs grid appear to "reload" with different
         products — jarring UX. Users want recs they see to stay visible.

         Re-fetch only triggers when: grid is empty (init), explicit
         invalidate() call, or user opens drawer after full invalidate. */
      if (alreadyRendered && signature !== this._lastSignature) {
        this._updateInCartState(els, handles);
        /* INFINITE REPLENISHMENT: if every currently-visible rec is now in the
           cart, pull the next batch from the reserve pool (or fetch more) so
           the user always sees fresh recs to explore. */
        await this._maybeReplenish(els, handles);
        this._lastSignature = signature;
        this._state = 'idle';
        return;
      }

      /* Same signature + already rendered = no-op */
      if (alreadyRendered) {
        this._state = 'idle';
        return;
      }

      /* First render: fetch products and populate grid */
      const products = await this._gatherProducts(cart);
      this._state = 'rendering';
      this._render(els, products, handles);
      this._lastSignature = signature;
    } finally {
      this._state = 'idle';
      /* If a sync arrived while we were busy, run one more pass. */
      if (this._dirty) {
        this._dirty = false;
        this._queueSync('dirty-flush');
      }
    }
  }

  /* Re-filter already-rendered items against new cart handles without refetching.
     Toggles data-in-cart attribute + button state.
     SOURCE OF TRUTH for the original button label: data-original-text (set at render time).
     This works in BOTH directions:
       - Add   → button becomes "✓" disabled
       - Remove → button restores to data-original-text ("AÑADIR" or a size label) */
  _updateInCartState(els, cartHandles) {
    const cartSet = new Set(cartHandles);
    for (const item of els.grid.children) {
      const productHandle = item.dataset.productHandle;
      const isInCart = productHandle && cartSet.has(productHandle);
      const wasInCart = item.hasAttribute('data-in-cart');
      item.toggleAttribute('data-in-cart', !!isInCart);
      /* RESET BUTTON DOM when flipping out-of-cart — the click handler may have
         left .textContent='✓', .disabled=true, .--added/--loading from an
         optimistic update. CSS alone (show/hide pill row) isn't enough because
         the next time this item comes back to recs (e.g., via replenishment),
         those DOM props would persist. Fix for audit H3/H4. */
      if (wasInCart && !isInCart) {
        const btns = item.querySelectorAll('.cart-recs__variant-btn');
        btns.forEach(btn => {
          btn.disabled = false;
          btn.classList.remove(
            'cart-recs__variant-btn--added',
            'cart-recs__variant-btn--loading'
          );
          if (btn.dataset.originalText) {
            btn.textContent = btn.dataset.originalText;
          }
        });
      }
    }
  }

  /* ═══ INFINITE REPLENISHMENT ═══
     Called after _updateInCartState. If every rec currently on screen has been
     added to the cart, swap them out for the next batch:
       1. Try the reserve pool (already-fetched candidates).
       2. If pool is empty, refetch recs using current cart as seed, filtering
          out any ID we've already shown (so we never re-show a rec twice).
     The batch replaces the old items with a smooth reconciliation (reuses
     the key-based diff path in _render's insertBefore loop). */
  async _maybeReplenish(els, cartHandles) {
    const items = Array.from(els.grid.children).filter(c => c.dataset && c.dataset.productHandle);
    if (items.length === 0) return;
    const cartSet = new Set(cartHandles);
    const allInCart = items.every(it => cartSet.has(it.dataset.productHandle));
    if (!allInCart) return;

    let nextBatch = [];

    /* 1. Drain from pool first */
    if (this._pool.length) {
      while (nextBatch.length < this._maxItems && this._pool.length) {
        const p = this._pool.shift();
        if (cartSet.has(p.handle)) continue; /* skip if already in cart */
        nextBatch.push(p);
      }
    }

    /* 2. If still not enough, refetch with exclusions */
    if (nextBatch.length < this._maxItems) {
      try {
        const cart = await this._fetchCart();
        const fresh = await this._gatherProducts(cart);
        for (const p of fresh) {
          if (nextBatch.length >= this._maxItems) break;
          const id = String(p.id);
          if (this._shownIds.has(id)) continue;
          if (cartSet.has(p.handle)) continue;
          if (this._isExcluded(p)) continue;
          if (nextBatch.some(x => x.id === p.id)) continue;
          nextBatch.push(p);
        }
      } catch (err) {
        console.warn('[FateCartRecs] replenish refetch failed', err);
      }
    }

    /* 3. FALLBACK: pool + API exhausted → fetch merchant fallback collection
       IGNORING shownIds (we cycle back through). Prevents the "stuck with 8 ✓"
       stall described in audit H2. */
    if (nextBatch.length === 0) {
      try {
        const fallback = await this._fetchFallback();
        for (const p of fallback) {
          if (nextBatch.length >= this._maxItems) break;
          if (cartSet.has(p.handle)) continue;
          if (this._isExcluded(p)) continue;
          if (nextBatch.some(x => x.id === p.id)) continue;
          nextBatch.push(p);
        }
        /* Reset shownIds so we can cycle through collection items again */
        if (nextBatch.length > 0) this._shownIds.clear();
      } catch (err) {
        console.warn('[FateCartRecs] fallback collection fetch failed', err);
      }
    }

    /* 4. FINAL ESCAPE: if even the fallback is fully in-cart, bust its cache
       and refetch with a larger page limit — the initial fetch capped at 16.
       Audit P1-3: prevents the "8 ✓ AÑADIDO stuck forever" state when the
       merchant's fallback collection is small. */
    if (nextBatch.length === 0) {
      try {
        this._fallbackCache = null;
        const larger = await this._fetchFallbackWide();
        for (const p of larger) {
          if (nextBatch.length >= this._maxItems) break;
          if (cartSet.has(p.handle)) continue;
          if (this._isExcluded(p)) continue;
          if (nextBatch.some(x => x.id === p.id)) continue;
          nextBatch.push(p);
        }
        if (nextBatch.length > 0) this._shownIds.clear();
      } catch (err) {
        console.warn('[FateCartRecs] wide fallback failed', err);
      }
    }

    if (nextBatch.length === 0) {
      /* Surface the exhaustion via event + wrapper class so merchant CSS (or
         future FATE code) can show a "no more recs" message. We do NOT tear
         down the grid — leaving the ✓ items visible is better than a
         collapse/jump on empty. */
      const wrapper = els.wrapper || document.getElementById('cart-recs');
      if (wrapper) wrapper.classList.add('cart-recs--exhausted');
      document.dispatchEvent(new CustomEvent('fate:recs:exhausted', {
        detail: { cartHandles: cartHandles.slice() },
      }));
      return;
    }
    /* Clear exhausted flag since we recovered. */
    const wrapperClear = els.wrapper || document.getElementById('cart-recs');
    if (wrapperClear) wrapperClear.classList.remove('cart-recs--exhausted');

    /* Swap: remove old items, render new */
    for (const it of items) it.remove();
    for (const p of nextBatch) {
      this._shownIds.add(String(p.id));
      els.grid.appendChild(this._renderItem(p));
    }
    this._observeImages(els.grid);
    document.dispatchEvent(new CustomEvent('fate:recs:replenished', {
      detail: { count: nextBatch.length },
    }));
  }

  async _fetchCart() {
    /* Prefer the shared __fateCartFetch helper (defined in fate-cart-utils)
       so this request joins the in-flight dedup across all FATE modules
       (announcement, coordinator, insurance). Saves redundant /cart.js
       round-trips during cart-event bursts. Audit fix 2026-05-15.
       Defensive fallback to local TTL-cached fetch if utils not loaded. */
    if (typeof window.__fateCartFetch === 'function') {
      return window.__fateCartFetch().catch(() => ({ items: [] }));
    }
    const now = Date.now();
    if (this._cartPromise && (now - this._cartPromiseTs) < CART_FETCH_DEDUPE_MS) {
      return this._cartPromise;
    }
    this._cartPromiseTs = now;
    /* Clear the cached promise on failure so retries can proceed instead of
       being stuck with a failed empty-items response for the next second.
       Fix for audit H10. */
    this._cartPromise = fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => {
        if (!r.ok) throw new Error('cart fetch ' + r.status);
        return r.json();
      })
      .catch(err => {
        this._cartPromise = null;
        this._cartPromiseTs = 0;
        return { items: [] };
      });
    return this._cartPromise;
  }

  async _gatherProducts(cart) {
    if (!cart.items || !cart.items.length) return await this._fetchFallback();
    const uniqueIds = [...new Set(cart.items.filter(i => i.requires_shipping && !/segunda-prenda-1-peso|sticker|seguro|mystery|mistery/i.test(i.handle || '')).map(i => i.product_id))].slice(0, 5);
    const results = await Promise.all(uniqueIds.map(id => this._fetchRecs(id)));
    /* F6: filtrar por el gate de marca (F4T3) ANTES de medir si falta cubrir el
       grid, para que el fallback rellene cuando las recomendaciones traen pocos
       productos de marca. Sin esto, all.length contaba items que _render luego
       descarta → grid casi vacío. */
    const inCart = new Set(cart.items.map(item => item.handle));
    let all = [...new Map(results.flat().filter(p => !inCart.has(p.handle) && !this._isExcluded(p)).map(p => [String(p.id), p])).values()];
    if (all.length < this._maxItems) {
      const fallback = await this._fetchFallback();
      all = all.concat(fallback);
    }
    return all;
  }

  async _fetchRecs(productId) {
    const cached = this._recsCache.get(productId);
    if (cached && (Date.now() - cached.ts) < RECS_CACHE_TTL_MS) {
      return cached.products;
    }
    const url = `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`;
    const products = await fetch(url, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => data.products || [])
      .catch(() => null);
    /* Fallo transitorio de red: no envenenar el caché con [] por 60s;
       servir el caché viejo (aunque haya expirado) o vacío sin cachear. */
    if (products === null) return cached ? cached.products : [];
    this._recsCache.set(productId, { products, ts: Date.now() });
    return products;
  }

  async _fetchFallback() {
    /* B4: TTL-aware cache. Without it, products that go out-of-stock mid-session
       still appear as available in recs because we'd serve the stale cache forever.
       Aligns with _recsCache's 60s TTL. */
    if (this._fallbackCache && (Date.now() - this._fallbackCache.ts) < RECS_CACHE_TTL_MS) {
      return this._fallbackCache.products;
    }
    const tpl = document.getElementById('cart-recs-fallback-collection');
    if (!tpl) { this._fallbackCache = { products: [], ts: Date.now() }; return []; }
    const handle = tpl.textContent.trim();
    if (!handle) { this._fallbackCache = { products: [], ts: Date.now() }; return []; }
    const products = await fetch(`/collections/${handle}/products.json?limit=${Math.min(50, Math.max(16, this._maxItems * 2))}`, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => data.products || [])
      .catch(() => null);
    /* Fallo transitorio: no cachear vacío; servir el caché anterior si existe. */
    if (products === null) return this._fallbackCache ? this._fallbackCache.products : [];
    this._fallbackCache = { products, ts: Date.now() };
    return products;
  }

  /* Audit P1-3 escape: when the 16-product fallback is fully in cart, fetch
     up to 50 to widen the candidate pool. Called only on exhaustion so the
     extra bytes don't hit every cart session. */
  async _fetchFallbackWide() {
    const tpl = document.getElementById('cart-recs-fallback-collection');
    if (!tpl) return [];
    const handle = tpl.textContent.trim();
    if (!handle) return [];
    return fetch(`/collections/${handle}/products.json?limit=50`, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => data.products || [])
      .catch(() => []);
  }

  /* ═══════════════════ RENDERING ═══════════════════ */

  _getEls() {
    const grid = document.getElementById('cart-recs-grid');
    const wrapper = document.getElementById('cart-recs');
    if (wrapper) this._maxItems = parseInt(wrapper.dataset.count, 10) || 6;
    return { grid, wrapper };
  }

  _makeSignature(handles) {
    return handles.slice().sort().join('|');
  }

  _getTags(p) {
    let tags = p.tags || [];
    if (typeof tags === 'string') tags = tags.split(',');
    return tags.map(t => String(t).trim().toLowerCase());
  }

  _isExcluded(p) {
    /* Exclusión por KEYWORD primero (seguro de envío, mystery, servicios) — corre
       ANTES del gate de marca para atrapar aunque el endpoint no traiga tags o el
       producto tenga el tag F4T3 por error. Cubre title + handle + tipo. */
    const t = `${p.title || ''} ${p.handle || ''} ${p.product_type || ''} ${p.type || ''}`.toLowerCase();
    if (EXCLUDED_KEYWORDS.some(k => t.includes(k))) return true;
    /* Gate de marca: si no trae el tag F4T3, se excluye. */
    if (!this._getTags(p).includes(BRAND_TAG)) return true;
    return false;
  }

  _render(els, products, cartHandles) {
    const filtered = products.filter(p => !cartHandles.includes(p.handle) && !this._isExcluded(p));
    const seen = new Set();
    const ordered = [];
    const pool = [];
    for (const p of filtered) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      if (ordered.length < this._maxItems) {
        ordered.push(p);
      } else {
        pool.push(p);
      }
    }
    /* Stash pool for infinite replenishment (consumed by _maybeReplenish). */
    this._pool = pool;
    for (const p of ordered) this._shownIds.add(String(p.id));

    if (ordered.length === 0) {
      els.wrapper.hidden = true;
      document.dispatchEvent(new CustomEvent('fate:recs:empty'));
      return;
    }

    els.wrapper.hidden = false;

    /* ── KEY-BASED DIFF RECONCILIATION ──
       Preserves DOM nodes for products still in the list (matched by product id).
       Avoids replaceChildren which destroys ALL nodes and causes a full visual
       re-render every time cart signature changes.

       Algorithm:
         1. Index existing DOM nodes by their data-morph-key (product id).
         2. Walk the ORDERED new product list. For each:
            - If a node already exists with that id: reuse it (move to correct position).
            - Otherwise: create a new node.
         3. Any existing node not in the new list gets removed.
         4. Nodes are appended/moved in the final order to match `ordered`. */
    const existingByKey = new Map();
    for (const child of Array.from(els.grid.children)) {
      /* El <template id="cart-recs-fallback-collection"> vive dentro del grid
         y no tiene morphKey — borrarlo mata el fallback para toda la sesión
         (_fetchFallback lo busca por id en cada TTL-expiry). */
      if (child.tagName === 'TEMPLATE') continue;
      const key = child.dataset?.morphKey;
      if (key) existingByKey.set(key, child);
      else child.remove(); /* orphan node without key — clean up */
    }

    const wantedKeys = new Set();
    for (let i = 0; i < ordered.length; i++) {
      const p = ordered[i];
      const key = String(p.id);
      wantedKeys.add(key);
      let node = existingByKey.get(key);
      if (node) {
        existingByKey.delete(key); /* mark as used */
      } else {
        node = this._renderItem(p);
      }
      /* Append (moves if already in DOM) — final order matches `ordered` */
      if (els.grid.children[i] !== node) {
        els.grid.insertBefore(node, els.grid.children[i] || null);
      }
    }

    /* Remove stale nodes (products no longer in list) */
    for (const [key, node] of existingByKey) {
      if (!wantedKeys.has(key)) node.remove();
    }

    this._observeImages(els.grid);
    this._attachDelegation(els.grid);
    document.dispatchEvent(new CustomEvent('fate:recs:rendered', {
      detail: { signature: this._lastSignature, count: ordered.length },
    }));
  }

  // Load only cards near the horizontal viewport, even when the list grows.
  _observeImages(grid) {
    const load = image => {
      image.src = image.dataset.recsSrc;
      delete image.dataset.recsSrc;
    };
    if (typeof IntersectionObserver === 'undefined') {
      grid.querySelectorAll('img[data-recs-src]').forEach(load);
      return;
    }
    if (this._imageRoot !== grid) {
      this._imageObserver?.disconnect();
      this._imageRoot = grid;
      this._imageObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          load(entry.target);
          this._imageObserver.unobserve(entry.target);
        }
      }, { root: grid, rootMargin: '0px 160px', threshold: 0 });
    }
    grid.querySelectorAll('img[data-recs-src]').forEach(image => this._imageObserver.observe(image));
  }

  _renderItem(p) {
    const tpl = document.createElement('div');
    tpl.className = 'cart-recs__item';
    tpl.dataset.morphKey = String(p.id);          /* stable identity for diff reconciliation */
    tpl.dataset.productHandle = String(p.handle); /* used by _updateInCartState to match against cart */
    /* FATE 2026-06-26: stamps para el badge stack del rec (NEW ARRIVAL + AHORRA $X).
       Las colecciones 3x1/3x2 + MID SEASON SALE las resuelve por MEMBRESÍA REAL
       fate-promo-collections-data.liquid (los tags no sirven: un tee del 3x1
       trae tag '3x2'). Pero el tag 'new arrival' y el compare-at solo están aquí,
       en el product data de /recommendations|collections/products.json. El stamp
       persiste en el elemento aunque el item se reuse por key-based diff. */
    try {
      let tags = p.tags || [];
      if (typeof tags === 'string') tags = tags.split(',');
      if (tags.join('|').toLowerCase().indexOf('new arrival') !== -1) tpl.dataset.fateNew = '1';
      const v0 = (p.variants && p.variants[0]) || {};
      /* Misma normalización cents/units que _formatMoney — parseFloat crudo
         producía ahorros 100x cuando el endpoint serializa en centavos. */
      const cp = this._moneyNumber(v0.compare_at_price || '0');
      const pr = this._moneyNumber(v0.price || '0');
      if (cp > pr && pr > 0) {
        tpl.dataset.fateSave = String(Math.round(cp - pr));
        tpl.dataset.fateDiscountPct = String(Math.round((cp - pr) * 100 / cp));
      }
    } catch (e) { /* defensivo: si falta data, simplemente no estampa */ }
    tpl.innerHTML = this._itemHTML(p);
    return tpl;
  }

  _itemHTML(p) {
    const img = this._firstImg(p);
    const imgTag = img
      ? `<img data-recs-src="${this._escAttr(img.replace(/\.([^.?]+)(\?|$)/, '_400x.$1$2'))}" alt="${this._escAttr(p.title || '')}" loading="lazy" decoding="async" width="400" height="400">`
      : '';
    const productUrl = p.url || `/products/${p.handle}`;
    const available = p.available !== false;
    const soldOutBadge = !available ? '<span class="cart-recs__badge">SOLD OUT</span>' : '';
    const v0 = (p.variants && p.variants[0]) || {};
    const variantHTML = available ? this._variantPickerHTML(p) : '';
    const priceHTML = this._priceHTML(v0);

    /* FATE 2026-06-16: badge de promo por colección (3x999 / 3x2).
       Aplicado como post-process por fate-promo-collections-data.liquid
       (listener en fate:recs:rendered) — más robusto que inyección aquí,
       porque el controller reusa items vía key-based diff y este
       _itemHTML solo corre en items NUEVOS, no en los reusados. */
    return `
      <div class="cart-recs__image">
        <a href="${this._escAttr(productUrl)}" class="cart-recs__image-link">${imgTag}${soldOutBadge}</a>
        ${variantHTML}
      </div>
      <div class="cart-recs__info">
        <a href="${this._escAttr(productUrl)}" class="cart-recs__product-title">${this._escHTML(p.title || '')}</a>
        <div class="cart-recs__price">${priceHTML}</div>
      </div>
    `;
  }

  _firstImg(p) {
    if (!p.images || !p.images.length) return '';
    const i0 = p.images[0];
    return typeof i0 === 'string' ? i0 : (i0.src || '');
  }

  _priceHTML(v) {
    const price = this._formatMoney(v.price || '0');
    const cp = v.compare_at_price;
    if (cp && parseFloat(cp) > parseFloat(v.price || '0')) {
      return `<s class="cart-recs__compare">${this._formatMoney(cp)}</s> <span class="cart-recs__sale-price">${price}</span>`;
    }
    return price;
  }

  /* Normaliza price (string units "999.00" | string cents "99900" | number cents)
     a número en unidades. Única fuente de verdad para _formatMoney y fateSave. */
  _moneyNumber(price) {
    let n;
    if (typeof price === 'string') {
      n = price.indexOf('.') === -1 ? parseInt(price, 10) / 100 : parseFloat(price);
    } else if (typeof price === 'number') {
      n = price / 100;
    } else {
      n = 0;
    }
    return isNaN(n) ? 0 : n;
  }

  _formatMoney(price) {
    return '$' + this._moneyNumber(price).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  _variantPickerHTML(p) {
    if (!p.variants || !p.variants.length) return '';
    const sizeIdx = this._findSizeIndex(p);
    /* ADDED-STATE BADGE — swapped in by CSS when the parent item has
       [data-in-cart]. One unified "✓ AÑADIDO" label replaces the whole pill
       row (no ghost ✓ on every size). */
    const addedBadge = `<span class="cart-recs__added-badge" aria-hidden="true">✓ AÑADIDO</span>`;

    if (sizeIdx >= 0 && p.variants.length > 1) {
      const key = `option${sizeIdx + 1}`;
      const sizes = [...new Set(p.variants.map(v => v[key]).filter(Boolean))];
      const data = { id: p.id, title: p.title, sp: sizeIdx + 1, fromDrawer: true,
        image: this._firstImg(p), price: this._formatMoney(p.variants[0].price),
        variants: p.variants.map(v => ({id:v.id,av:v.available !== false,o:[v.option1,v.option2,v.option3]})) };
      const pills = sizes.map(size => {
        const available = p.variants.some(v => v[key] === size && v.available !== false);
        return `<button type="button" class="fate-qs__sz${available ? '' : ' fate-qs__sz--out'}" data-size="${this._escAttr(size)}"${available ? '' : ' disabled'}>${this._escHTML(size)}</button>`;
      }).join('');
      return `<div class="fate-qs" data-fate-qs data-product-id="${p.id}"><div class="fate-qs__bar">${pills}</div><button type="button" class="fate-qs__plus" aria-label="Seleccionar talla de ${this._escAttr(p.title)}"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M2.75 7h8.5M7 2.75v8.5"/></svg></button><script type="application/json" data-fate-qs-data>${JSON.stringify(data).replace(/</g, '\\u003c')}</script></div>${addedBadge}`;
    }
    /* Single variant or no size → single AÑADIR button */
    const firstAvail = p.variants.find(v => v.available !== false) || p.variants[0];
    return `<div class="cart-recs__variants" data-product-id="${p.id}">`
         + `<button type="button" class="cart-recs__variant-btn cart-recs__variant-btn--single" data-variant-id="${firstAvail.id}" data-original-text="AÑADIR">AÑADIR</button>`
         + addedBadge
         + `</div>`;
  }

  _findSizeIndex(p) {
    if (!p.options) return -1;
    for (let i = 0; i < p.options.length; i++) {
      const name = (typeof p.options[i] === 'string' ? p.options[i] : (p.options[i].name || '')).toLowerCase();
      if (SIZE_KEYWORDS.some(k => name.includes(k))) return i;
    }
    return -1;
  }

  /* ═══════════════════ EVENT DELEGATION ═══════════════════ */

  _attachDelegation(grid) {
    /* B5: re-bind if the grid element is a different node than last time.
       data-morph-preserve protects the subtree from regular cart morphs, but
       theme-editor reloads / Foxify section swaps can replace the entire grid
       node. The old listener becomes orphan; new clicks would no-op. */
    if (this._delegationGrid === grid) return;
    this._delegationGrid = grid;
    grid.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cart-recs__variant-btn:not([disabled])');
      if (!btn) return;
      /* If item is already in cart (CSS hides pills, shows badge), ignore. */
      const item = btn.closest('.cart-recs__item');
      if (item && item.hasAttribute('data-in-cart')) return;
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      if (btn.dataset.pending) return;
      btn.dataset.pending = 'true';
      btn.disabled = true;
      btn.classList.add('cart-recs__variant-btn--loading');
      btn.textContent = '...';
      try {
        if (typeof window.__fateCartAdd !== 'function') throw new Error('__fateCartAdd unavailable');
        await window.__fateCartAdd(variantId, { openDrawer: false });
        /* Set ✓ state. Original label stays in data-original-text so
           _updateInCartState can restore it if user later removes from cart. */
        btn.textContent = '✓';
        btn.classList.remove('cart-recs__variant-btn--loading');
        btn.classList.add('cart-recs__variant-btn--added');
        btn.disabled = true;
      } catch (err) {
        btn.disabled = false;
        btn.classList.remove('cart-recs__variant-btn--loading');
        btn.textContent = 'Error';
        setTimeout(() => { btn.textContent = btn.dataset.originalText || 'AÑADIR'; }, 2000);
      } finally { delete btn.dataset.pending; }
    });
  }

  /* ═══════════════════ UTIL ═══════════════════ */

  _escHTML(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  _escAttr(s) { return this._escHTML(s); }
}

/* ═══════════════════ SINGLETON BOOTSTRAP ═══════════════════ */
if (!window.__fateRecsController) {
  const boot = () => {
    window.__fateRecsController = new FateCartRecsController();
    window.FateCartRecs = {
      sync: () => window.__fateRecsController.sync(),
      invalidate: () => window.__fateRecsController.invalidate(),
      getState: () => window.__fateRecsController.getState(),
    };
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
